import { ClockIcon, MinusIcon, PlusIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { AnimatePresence, motion } from "framer-motion";

import type { AiWalletTransaction } from "@/client/services/ai-credit/api";

import {
  formatExpiryDate,
  formatTransactionDate,
  POSITIVE_TRANSACTIONS,
  TRANSACTION_LABELS,
} from "../utils";

type RowProps = { transaction: AiWalletTransaction; index: number };

const TransactionRow = ({ transaction, index }: RowProps) => {
  const positive = POSITIVE_TRANSACTIONS.has(transaction.type) && transaction.points >= 0;
  const expiry = formatExpiryDate(transaction.expiresAt);

  return (
    <motion.li
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.035, 0.18) }}
      className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0 sm:px-5"
    >
      <span
        className={cn(
          "grid size-8 shrink-0 place-items-center rounded-lg",
          positive ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600",
        )}
      >
        {positive ? <PlusIcon size={14} weight="bold" /> : <MinusIcon size={14} weight="bold" />}
      </span>
      <div className="min-w-0">
        <p className="truncate text-[11px] font-bold text-[#071451] sm:text-xs">
          {TRANSACTION_LABELS[transaction.type]}
        </p>
        <p className="mt-0.5 truncate text-[9px] text-slate-500 sm:text-[10px]">
          {formatTransactionDate(transaction.createdAt)}
          {expiry ? ` · expire le ${expiry}` : ""}
          {transaction.type === "CONSUME" && transaction.feature ? ` · ${transaction.feature}` : ""}
        </p>
      </div>
      <div className="text-right">
        <p
          className={cn(
            "text-xs font-bold tabular-nums sm:text-sm",
            positive ? "text-emerald-600" : "text-rose-600",
          )}
        >
          {positive ? "+" : ""}
          {transaction.points.toLocaleString("fr-FR")}
        </p>
        <p className="mt-0.5 text-[8px] font-medium text-slate-400">
          Solde {transaction.balanceAfter.toLocaleString("fr-FR")}
        </p>
      </div>
    </motion.li>
  );
};

type Props = {
  transactions?: AiWalletTransaction[];
  loading: boolean;
  error: unknown;
  onRetry: () => void;
};

export const TransactionHistory = ({ transactions, loading, error, onRetry }: Props) => (
  <section
    aria-labelledby="history-title"
    className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_5px_20px_rgba(15,23,42,0.035)]"
  >
    <header className="flex items-center gap-3 border-b border-slate-100 px-4 py-4 sm:px-5">
      <span className="grid size-8 place-items-center rounded-lg bg-indigo-50 text-indigo-700">
        <ClockIcon size={17} weight="duotone" />
      </span>
      <div>
        <h2 id="history-title" className="text-sm font-bold text-[#071451]">
          Historique récent
        </h2>
        <p className="mt-0.5 text-[9px] text-slate-500 sm:text-[10px]">
          Vos achats, crédits offerts et utilisations IA.
        </p>
      </div>
    </header>

    <AnimatePresence mode="wait">
      {loading ? (
        <motion.div
          key="loading"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-2 p-4"
        >
          {[0, 1, 2].map((item) => (
            <div key={item} className="h-14 animate-pulse rounded-xl bg-slate-100" />
          ))}
        </motion.div>
      ) : error ? (
        <motion.div
          key="error"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center gap-3 p-5 text-xs text-rose-600"
        >
          <WarningCircleIcon size={18} className="shrink-0" />
          <span className="flex-1">Impossible de charger l’historique.</span>
          <Button variant="outline" size="sm" className="rounded-lg" onClick={onRetry}>
            Réessayer
          </Button>
        </motion.div>
      ) : !transactions?.length ? (
        <motion.div
          key="empty"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="px-5 py-10 text-center"
        >
          <p className="text-xs font-bold text-[#071451]">Aucune transaction</p>
          <p className="mt-1 text-[10px] text-slate-500">
            Votre activité apparaîtra ici après votre première utilisation.
          </p>
        </motion.div>
      ) : (
        <motion.ul key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          {transactions.map((transaction, index) => (
            <TransactionRow key={transaction.id} transaction={transaction} index={index} />
          ))}
        </motion.ul>
      )}
    </AnimatePresence>
  </section>
);
